import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AnalyticsTracker from './components/seo/AnalyticsTracker';
import { initWebVitals } from './utils/webVitals';
import { lazyWithReload } from './utils/lazyWithReload';
import { RBACProvider, useRBAC } from './contexts/RBACContext';
import { SportProvider } from './contexts/SportContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { SportsLangProvider } from './contexts/SportsLangContext';
import { GovernanceLangProvider } from './contexts/GovernanceLangContext';
import { ActiveContextProvider } from './contexts/ActiveContextContext';
import { FEATURES } from './config/features';
import { default as OPS } from './navigation/opsRoutes';
import ErrorBoundary from './components/ErrorBoundary';
import AccessGuard from './components/AccessGuard';  // NOVO: Guard de controle de acesso
import Layout from './components/Layout';
import RouteGuard from './components/guards/RouteGuard';
import { shouldFunnelToWizard, WIZARD_PATH } from './utils/onboardingFunnel';
import { getDashboardRouteForRole } from './navigation/sidebarConfig';
import { isRouteOutOfScope } from './config/demoScope';
import {
  R, CENTRAL_ROLES, ANALYTICS_ROLES, VIDEO_ANALYSIS_ROLES,
  TACTICS_ROLES, LIBRARY_ROLES, PREP_ROLES,
} from './navigation/routeRegistry';
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import VukzHomePage from './pages/VukzHomePage';
import PtVukzHomePage from './pages/PtVukzHomePage';
import EnPrivacyPolicyPage from './pages/en/EnPrivacyPolicyPage';
import EnTermsOfServicePage from './pages/en/EnTermsOfServicePage';
import EnSLAPage from './pages/en/EnSLAPage';
import EnSportsLandingPage from './pages/en/EnSportsLandingPage';
import EnGovernanceLandingPage from './pages/en/EnGovernanceLandingPage';
import FootballHubPage from './pages/FootballHubPage';
import FootballCommunityPage from './pages/FootballCommunityPage';
import SocialChampionshipPage from './pages/SocialChampionshipPage';
import MatchPage from './pages/MatchPage.jsx';
import MatchesListPage from './pages/MatchesListPage';
import LoginPage from './pages/LoginPage';
import RegisterPageV2 from './pages/RegisterPageV2';
import AcceptInvitePage from './pages/AcceptInvitePage';
import InviteRegisterPage from './pages/InviteRegisterPage';
import PreLaunchPage from './pages/PreLaunchPage';
import RequestAccessPage from './pages/RequestAccessPage';
import PendingApprovalPage from './pages/PendingApprovalPage';  // NOVO: Tela "Em Breve" para usuários pendentes
import SubscriptionSuspendedPage from './pages/SubscriptionSuspendedPage';  // Marketplace: assinatura suspensa
import MarketplaceActivationPage from './pages/MarketplaceActivationPage';
import GovernanceMarketplacePage from './pages/governance/GovernanceMarketplacePage';
import GovernanceUsageDashboardPage from './pages/governance/GovernanceUsageDashboardPage';
import UserProfilePage from './pages/UserProfilePage';
import MyUsagePage from './pages/MyUsagePage';
import TeamUsagePage from './pages/TeamUsagePage';
import ClubUsagePage from './pages/ClubUsagePage';
import SocialProfilePage from './pages/SocialProfilePage';
import ArenaProfilePage from './pages/ArenaProfilePage';
import CreateInstitutionPage from './pages/CreateInstitutionPage';
// Sport pages (active pages kept)
import SwimmingPage from './pages/SwimmingPage';
import SwimmingHubPage from './pages/SwimmingHubPage';
import SwimmingCommunityPage from './pages/SwimmingCommunityPage';
import SwimmingChampionshipsPage from './pages/SwimmingChampionshipsPage';
import SwimmingChampionshipDetailsPage from './pages/SwimmingChampionshipDetailsPage';
import SwimmingRankingsPage from './pages/SwimmingRankingsPage';
import SwimmingCompetitionResultsPage from './pages/SwimmingCompetitionResultsPage';
import AthletePerformancePage from './pages/AthletePerformancePage';
import CoachPerformancePage from './pages/CoachPerformancePage';
import ManagerHomePage from './pages/ManagerHomePage';
import ManagerTrainingOverviewPage from './pages/ManagerTrainingOverviewPage';
import GestorPlanPage from './pages/GestorPlanPage';
import ManagerStrategicPlanPage from './pages/manager/ManagerStrategicPlanPage';
import GestorCalendarPage from './pages/manager/GestorCalendarPage';
import CalendarUnifiedPage from './pages/manager/CalendarUnifiedPage';
import FinancialPage from './pages/manager/FinancialPage';
import SupportPage from './pages/manager/SupportPage';
import AthleteProfilePage from './pages/athlete/AthleteProfilePage';
import CategoriesPage from './pages/CategoriesPage';
import CategoryDetailPage from './pages/CategoryDetailPage';
// MethodologyGovernancePage: FORA DO MVP (SEC-A lote desliga 11/07/2026) - rota redireciona para /manager
import InterventionsPage from './pages/InterventionsPage';
import RosterPage from './pages/RosterPage';
import TeamPage from './pages/manager/TeamPage';
import StaffDetailPage from './pages/manager/StaffDetailPage';
import ClubSetupWizardPage from './pages/rbac/ClubSetupWizardPage';
import ClaimOfficialDataPage from './pages/manager/ClaimOfficialDataPage';
import VenuesPage from './pages/VenuesPage';
import VenueDetailPage from './pages/VenueDetailPage';
// Páginas Institucionais
import AboutPage from './pages/AboutPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SLAPage from './pages/SLAPage';
import ContactPage from './pages/ContactPage';
import PrivacySettingsPage from './pages/PrivacySettingsPage';
// Swim Vision (Computer Vision + Digital Twin)
import VideoAnalysisPage from './pages/VideoAnalysisPage';
import SwimVisionDashboard from './pages/SwimVisionDashboard';
// Track Vision (Athletics Biomechanics AI)
import TrackVisionDashboard from './pages/TrackVisionDashboard';
import TrackVisionSessionsPage from './pages/TrackVisionSessionsPage';
import TrackVisionSessionPage from './pages/TrackVisionSessionPage';
import TrackVisionAnnotationPage from './pages/TrackVisionAnnotationPage';
// Football Profile (dedicated player profile)
import FootballProfilePage from './pages/FootballProfilePage';
import SwimVisionSessionsPage from './pages/SwimVisionSessionsPage';
import BatchAnalysisPage from './pages/BatchAnalysisPage';
import SwimAnnotationsPage from './pages/SwimAnnotationsPage';
import RaceDashboardPage from './pages/RaceDashboardPage';
import RaceResultPage from './pages/RaceResultPage';
import SessionStatusPage from './pages/SessionStatusPage';
// Agenda Unificada (VUKZ)
import UnifiedAgendaPage from './pages/agenda/UnifiedAgendaPage';
// Treino Físico — Unified physical training (athlete + coach)
import TreinoFisicoPage from './pages/TreinoFisicoPage';
// Meus Campeonatos (sidebar shortcut)
import MyChampionshipsPage from './pages/MyChampionshipsPage';
// Estatísticas - Página antiga
import StatsPage from './pages/StatsPage';
// Mind - AI Analytics Page
import MindPage from './pages/MindPage';
// Serviços - Editorial Engine
import EditorialEnginePage from './pages/StatsPageNew';
import RankingDetailsPage from './pages/RankingDetailsPage';
// Jogadores
import PeopleDirectory from './pages/PeopleDirectory';
import PlayerDetailPage from './pages/PlayerDetailPage';
import CommunityUserDetailPage from './pages/CommunityUserDetailPage';
// Organizações
import OrganizationsPage from './pages/OrganizationsPage';
import OrganizationDetailPage from './pages/OrganizationDetailPage';
// Comunidades & Esportes
import ComunidadesPage from './pages/ComunidadesPage';
import EsportesPage from './pages/EsportesPage';
// Feed Social
import FeedPage from './pages/FeedPage';
// Notificações
import NotificationsPage from './pages/NotificationsPage';
// Chat/Mensagens
import ChatPage from './pages/ChatPage';
// Produtos - Creator & Analytics
import CreatorHub from './pages/Creator/CreatorHub';
import AnalyticsHub from './pages/Analytics/AnalyticsHub';
// Biblioteca - Digital Sports Library
import LibraryHubPage from './pages/library/LibraryHubPage';
import LibrarySearchPage from './pages/library/LibrarySearchPage';
import LibraryItemDetailPage from './pages/library/LibraryItemDetailPage';
import LibraryCreateItemPage from './pages/library/LibraryCreateItemPage';
// MethodologyLibraryPage/MethodologyDetailPage: FORA DO MVP (SEC-A lote desliga 11/07/2026) - rotas redirecionam para /library
// Governança & FinOps (Founder-only)
import { GovernanceGuard } from './components/governance/GovernanceGuard';
import GovernanceLayout from './components/governance/GovernanceLayout';
import GovernanceOverviewPage from './pages/governance/GovernanceOverviewPage';
import GovernanceControlTowerPage from './pages/governance/GovernanceControlTowerPage';
import GovernanceAgentsPage from './pages/governance/GovernanceAgentsPage';
import GovernanceCostsPage from './pages/governance/GovernanceCostsPage';
import GovernanceFinancePage from './pages/governance/GovernanceFinancePage';
import GovernancePricingPage from './pages/governance/GovernancePricingPage';

import GovernancePlannerPage from './pages/governance/GovernancePlannerPage';
import GovernanceBuilderPage from './pages/governance/GovernanceBuilderPage';
import GovernanceBuilderPageV2 from './pages/governance/GovernanceBuilderPageV2';
import { FEATURES as GOV_FEATURES } from './config/features';
import BrainPage from './pages/BrainPage';
import GovernanceContentHubPage from './pages/governance/GovernanceContentHubPage';
import GovernanceContentStudioPage from './pages/governance/GovernanceContentStudioPage';
import GovernanceMarketingPage from './pages/governance/GovernanceMarketingPage';
import MarketingSeoDashboardPage from './pages/governance/marketing/MarketingSeoDashboardPage';
import GovernanceEditorialEnginePage from './pages/governance/GovernanceEditorialEnginePage';
import GovernanceStaffScopesPage from './pages/governance/GovernanceStaffScopesPage';
import GovernanceObservabilityPage from './pages/governance/GovernanceObservabilityPage';
import GovernanceTIMonitoringPage from './pages/governance/GovernanceTIMonitoringPage';
import GovernanceClientsPage from './pages/governance/GovernanceClientsPage';
import GovernanceClientDetailPage from './pages/governance/GovernanceClientDetailPage';
import GovernancePurviewPage from './pages/governance/GovernancePurviewPage';
import GovernanceInvestorsPage from './pages/governance/GovernanceInvestorsPage';
import GovernanceComercialPage from './pages/governance/GovernanceComercialPage';
import GovernanceClientesPage from './pages/governance/GovernanceClientesPage';
import SwimmingLeadsPage from './pages/governance/commercial/SwimmingLeadsPage';
import GovernanceRHPage from './pages/governance/GovernanceRHPage';
// Stripe Billing
import PricingPage from './pages/billing/PricingPage';
import BillingSuccessPage from './pages/billing/BillingSuccessPage';
import BillingCancelPage from './pages/billing/BillingCancelPage';
import BillingPortalPage from './pages/billing/BillingPortalPage';
import GovernanceUsersPage from './pages/governance/GovernanceUsersPage';
import GovernanceOnboardingPage from './pages/governance/GovernanceOnboardingPage';
import GovernanceAuthorizationsPage from './pages/governance/GovernanceAuthorizationsPage';
import StaffPermissionsPage from './pages/manager/StaffPermissionsPage';
// Estudos Públicos (Editorial Engine)
import PublicInsightsListPage from './pages/PublicInsightsListPage';
import PublicInsightArticlePage from './pages/PublicInsightArticlePage';
import StudyAvgTimesEvolution from './pages/studies/StudyAvgTimesEvolution';
import StudyBuilder from './pages/studies/StudyBuilder';
import ResearchPage from './pages/ResearchPage';
import TacticsDesignerPage from './pages/TacticsDesignerPage';
import TacticsViewerPage from './pages/TacticsViewerPage';
import DiagnosticPage from './pages/DiagnosticPage';
// Parser & Ingestion Monitor

// Playbook System (American Football)
// PlayDesignerCanvas/PlayViewer: FORA DO MVP (SEC-A lote desliga 11/07/2026) - rotas redirecionam para /central
// RBAC Area Pages (B2B)
const AthleteAreaPage = lazyWithReload(() => import('./pages/rbac/AthleteAreaPage'));
const CoachAreaPage = lazyWithReload(() => import('./pages/rbac/CoachAreaPage'));
const ManagerAreaPage = lazyWithReload(() => import('./pages/rbac/ManagerAreaPage'));
const RecruiterAreaPage = lazyWithReload(() => import('./pages/rbac/RecruiterAreaPage'));
// Technical Library & Training
const TechnicalLibraryPage = lazyWithReload(() => import('./pages/rbac/TechnicalLibraryPage'));
const CoachTrainingPage = lazyWithReload(() => import('./pages/rbac/CoachTrainingPage'));
const CoachGroupsPage = lazyWithReload(() => import('./pages/rbac/CoachGroupsPage'));
const CoachGroupDetailPage = lazyWithReload(() => import('./pages/rbac/CoachGroupDetailPage'));
const PrepGroupsPage = lazyWithReload(() => import('./pages/rbac/PrepGroupsPage'));
const PrepGroupDetailPage = lazyWithReload(() => import('./pages/rbac/PrepGroupDetailPage'));
const PrepTrainingPage = lazyWithReload(() => import('./pages/rbac/PrepTrainingPage'));
const PrepPhysicalBuilderPage = lazyWithReload(() => import('./pages/rbac/PrepPhysicalBuilderPage'));
const PrepDashboardPage = lazyWithReload(() => import('./pages/prep/PrepDashboardPage'));
const SquadWellnessPage = lazyWithReload(() => import('./pages/rbac/SquadWellnessPage'));
const StaffPlanningPage = lazyWithReload(() => import('./pages/staff/StaffPlanningPage'));
const TrainingTimelinePage = lazyWithReload(() => import('./pages/athlete/TrainingTimelinePage'));
const TodayPage = lazyWithReload(() => import('./pages/athlete/TodayPage'));
const MyDietPage = lazyWithReload(() => import('./pages/athlete/MyDietPage'));
const StaffTodayPage = lazyWithReload(() => import('./pages/staff/StaffTodayPage'));
const AthleteDietsPage = lazyWithReload(() => import('./pages/staff/AthleteDietsPage'));

// SHOWCASE de marketing (captura de screenshots) — só com REACT_APP_SHOWCASE_MODE=true.
// Fixtures 100% sintéticas; zero API, zero auth; nunca importadas pelas páginas autenticadas.
const SHOWCASE_MODE = process.env.REACT_APP_SHOWCASE_MODE === 'true';
const AthleteTodayShowcase = lazyWithReload(() => import('./marketing/showcase/pages/AthleteTodayShowcase'));
const CoachTodayShowcase = lazyWithReload(() => import('./marketing/showcase/pages/CoachTodayShowcase'));
const PrepTodayShowcase = lazyWithReload(() => import('./marketing/showcase/pages/PrepTodayShowcase'));
const NutritionistTodayShowcase = lazyWithReload(() => import('./marketing/showcase/pages/NutritionistTodayShowcase'));
const ManagerTodayShowcase = lazyWithReload(() => import('./marketing/showcase/pages/ManagerTodayShowcase'));
// Athlete Area v2 (AthleteShell/TodayPage/SessionsPage/InsightsPage) e
// AthleteTrainingPage: aposentados 04/07/2026 — rotas viram redirect pra
// timeline; arquivos ainda no repo (remoção física em commit separado).
const AthleteIntegratedPlanPage = lazyWithReload(() => import('./pages/athlete/AthleteIntegratedPlanPage'));
const WellnessPage = lazyWithReload(() => import('./pages/athlete/WellnessPage'));
const AthleteResultsPage = lazyWithReload(() => import('./pages/athlete/AthleteResultsPage'));
const AthleteBiomechanicsPage = lazyWithReload(() => import('./pages/athlete/AthleteBiomechanicsPage'));
const CoachAthleteDetailPage = lazyWithReload(() => import('./pages/coach/CoachAthleteDetailPage'));
// Central Hub (Performance OS)
const CentralPage = lazyWithReload(() => import('./pages/CentralPage'));
// Home Pages with VUKZ Assist (Performance OS v2)
const AthleteHomePage = lazyWithReload(() => import('./pages/AthleteHomePage'));
const CoachHomePage = lazyWithReload(() => import('./pages/CoachHomePage'));
const PrepHomePage = lazyWithReload(() => import('./pages/PrepHomePage'));
const NutritionistHomePage = lazyWithReload(() => import('./pages/NutritionistHomePage'));
// VUKZ Assist — Central do Performance OS (substitui "Início" para os 4 perfis do lançamento)
const VukzAssistPage = lazyWithReload(() => import('./pages/assist/VukzAssistPage'));
// Training Execution Canvas (Performance OS v2)
const TrainingExecutionPage = lazyWithReload(() => import('./pages/training/TrainingExecutionPage'));
// Training Plan CRUD — /treinos, /treinos/novo, /treinos/:id
const TrainingPlanListPage   = lazyWithReload(() => import('./pages/training/TrainingPlanListPage'));
const TrainingPlanCreatePage = lazyWithReload(() => import('./pages/training/TrainingPlanCreatePage'));
const TrainingPlanDetailPage = lazyWithReload(() => import('./pages/training/TrainingPlanDetailPage'));
// Biblioteca de modelos — /treinos/modelos/:id (detalhe + edição estreita)
const TemplateDetailPage     = lazyWithReload(() => import('./pages/training/TemplateDetailPage'));
// Nutrition Workspace (Nutricionista)
const NutritionDashboardPage  = lazyWithReload(() => import('./pages/nutrition/NutritionDashboardPage'));
// N4: NutritionEntryPage não é mais importada — rota /nutrition/plans virou
// redirect (formulário morto: gravava por nome sem vínculo, backend rejeitava).
// Arquivo preservado em src/pages/nutrition/ como referência.
const NutritionPlanCreatePage = lazyWithReload(() => import('./pages/nutrition/NutritionPlanCreatePage'));
const NutritionAthletesPage   = lazyWithReload(() => import('./pages/nutrition/NutritionAthletesPage'));
const NutritionAlertsPage     = lazyWithReload(() => import('./pages/nutrition/NutritionAlertsPage'));
const NutritionDietsPage      = lazyWithReload(() => import('./pages/nutrition/NutritionDietsPage'));
const NutritionPlanDetailPage = lazyWithReload(() => import('./pages/nutrition/NutritionPlanDetailPage'));
// Biomechanics Specialist Workspace (Biomecânico)
const BiomechanicsDashboardPage = lazyWithReload(() => import('./pages/biomechanics/BiomechanicsDashboardPage'));
const BiomechanicsEntryPage     = lazyWithReload(() => import('./pages/biomechanics/BiomechanicsEntryPage'));
const BiomechanicsWorkspacePage  = lazyWithReload(() => import('./pages/biomechanics/BiomechanicsWorkspacePage'));
const SwimBiomechDashboard      = lazyWithReload(() => import('./pages/biomech/SwimBiomechDashboard'));

// Role constants now imported from routeRegistry.js (single source of truth)

// Maps user_type → role key for internal users on the product domain (vukz.ai).
// Mirrors the same mapping in RBACContext to avoid divergence.
const USER_TYPE_TO_ROLE = {
  coach: 'TREINADOR',
  preparador_fisico: 'PREPARADOR_FISICO',
  aux_prep_fisica: 'PREPARADOR_FISICO',
  athlete: 'ATLETA',
  club_admin: 'GESTOR',
  fan: 'ATLETA',
  nutricionista: 'NUTRICIONISTA',
  nutritionist: 'NUTRICIONISTA',
  biomecanico: 'BIOMECANICO',
  biomechanics_specialist: 'BIOMECANICO',
};

const CentralGate = () => {
  const { activeRole, rbacData, loading, isInternal } = useRBAC();

  // Wait for RBAC data before making routing decisions (prevents ATLETA fallback flash)
  if (loading || !rbacData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-10 h-10 border-4 border-t-green-700 border-gray-200 rounded-full" />
      </div>
    );
  }

  // ═══ DOMAIN-AWARE INTERNAL USER ROUTING ══════════════════════════════════
  // ops.vukz.ai  → governance panel (operations environment)
  // vukz.ai      → normal product experience (same user, different context)
  // The domain defines the environment; the role defines permissions within it.
  const isOpsDomain = window.location.hostname === 'ops.vukz.ai';
  if (isInternal && isOpsDomain) {
    return <Navigate to="/governance" replace />;
  }

  // On the product domain, resolve the effective role from user_type so that
  // internal users (OPERATIONS_ADMIN) get the right product experience.
  const userType = rbacData?.user?.user_type;
  const effectiveRole = isInternal
    ? (USER_TYPE_TO_ROLE[userType] || 'ATLETA')
    : (activeRole || 'ATLETA');

  // Club gestor OR coach-as-manager with PENDING onboarding → setup wizard (ADR-5).
  // C2 (jul/2026): predicate extracted to shouldFunnelToWizard — SINGLE SOURCE
  // shared with RouteGuard, so the two onboarding gates can never disagree.
  // Token-truth: chronology (7db79ee) proved the deliberate axis was
  // userType-vs-RBAC, never activeRole-vs-token; for the pending population
  // (single-role by construction) token-truth is provably equivalent.
  // D1 (jul/2026): removed dead branch 'in_progress' (CHECK constraint only allows
  // none/pending/completed) and the "migration 163 not applied" GESTOR+none fallback
  // (matrix proved 141/141 users have a status; the fallback was re-capturing
  // real managers like demo.gestor). 'none' now means FREE — the wizard is
  // reachable via explicit navigation, never forced.
  if (shouldFunnelToWizard(rbacData)) {
    return <Navigate to={WIZARD_PATH} replace />;
  }

  // Redirect each role to their dedicated home page with VUKZ Assist
  const roleHome = getDashboardRouteForRole(effectiveRole);
  return <Navigate to={roleHome} replace />;
};

/**
 * AthleteRedirect — Redireciona /athlete → /athlete/today (página-do-dia).
 * 07/07/2026: TodayPage ressuscitada standalone como HOME do atleta (treino
 * do dia + dieta do dia + widgets). Timeline segue em /athlete/training
 * como drill-down. Mantém retrocompatibilidade com bookmarks antigos.
 */
function AthleteRedirect() {
  return <Navigate to="/athlete/today" replace />;
}

/**
 * LegacyPlanRedirect — rotas antigas com :planId (AthleteTrainingPage,
 * TrainingExecutionPage do atleta) convergem pra timeline via deep-link
 * ?plan= — a âncora leva ao dia do treino e o estado temporal decide
 * editável/readOnly. 5 chamadores legados (TodayPage, SessionsPage,
 * AthleteAreaPage, AthleteTrainingPage, assist renderers) convergem aqui
 * sem tocar em cada tela — Strangler Fig no ponto de chegada.
 */
function LegacyPlanRedirect() {
  const { planId } = useParams();
  return <Navigate to={`/athlete/training?plan=${planId}`} replace />;
}

// Layout route wrapper for Governance — uses Outlet (RR v7 canonical pattern)
function GovernanceLayoutRoute() {
  return (
    <GovernanceGuard>
      <GovernanceLayout>
        <Outlet />
      </GovernanceLayout>
    </GovernanceGuard>
  );
}

// Error boundary wrapper for individual pages (diagnoses crashes)
class SafeRender extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error(`[SafeRender:${this.props.name}]`, error, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '2rem', color: '#dc2626', background: '#fef2f2', borderRadius: 8, margin: '1rem' }}>
          <h2 style={{ fontWeight: 'bold', marginBottom: 8 }}>❌ Crash in: {this.props.name}</h2>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{this.state.error.toString()}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

/* Geo-detection: Brazilian Portuguese browsers → /pt, rest → / (EN) */
function GeoRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  React.useEffect(() => {
    if (location.pathname === '/') {
      const isPortuguese = navigator.language?.startsWith('pt');
      if (isPortuguese) navigate('/pt', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/**
 * StandaloneAuthRoute — auth-only guard SEM Layout (sem sidebar/header).
 * Usado por páginas full-screen pós-login (ex: /manager/setup, ADR-3).
 * Preserva AccessGuard (que permite club_admin com onboarding pendente passar).
 * NÃO adicione RouteGuard de role aqui — ver ADR-3 (chicken-and-egg).
 */
function StandaloneAuthRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-12 h-12 border-4 border-t-green-700 border-gray-200 rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <AccessGuard>{children}</AccessGuard>;
}

/** G2 — Agenda não existe mais como conceito de navegação para o GESTOR:
 *  redireciona ao Calendário Unificado. Demais roles seguem na Agenda
 *  (migração completa acontece em pacote próprio). */
function AgendaOrCalendarRedirect() {
  const { activeRole } = useRBAC();
  if (activeRole === 'GESTOR') return <Navigate to="/manager/calendar" replace />;
  return <UnifiedAgendaPage />;
}

function ProtectedAppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  const { activeRole } = useRBAC();
  const location = useLocation();

  // ops.vukz.ai → redirect to /governance automatically
  const isOpsPortal = window.location.hostname === 'ops.vukz.ai';
  if (isOpsPortal && !window.location.pathname.startsWith('/governance')) {
    return <Navigate to="/governance" replace />;
  }

  // 🔍 DEBUG: Verificar se Layout está undefined (causa React #130)
  if (!Layout) {
    console.error('[App] ❌ Layout is UNDEFINED!');
    return <div style={{ padding: '2rem', color: 'red' }}>Layout component is undefined (React #130)</div>;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-12 h-12 border-4 border-t-green-700 border-gray-200 rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // DEMO SCOPE GUARD — rotas fora do escopo inicial não montam componente
  // nem disparam API; redirecionam à home da role. NÃO substitui RBAC backend.
  // Config central: src/config/demoScope.js (REACT_APP_FULL_SCOPE=true desliga).
  if (isRouteOutOfScope(location.pathname)) {
    return <Navigate to={getDashboardRouteForRole(activeRole)} replace />;
  }

  return (
    <AccessGuard>
      <Layout>
        <React.Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="animate-spin w-10 h-10 border-4 border-t-green-700 border-gray-200 rounded-full"></div>
          </div>
        }>
        <Routes>
          {/* ═══════════════════════════════════════════════════ */}
          {/* DEPRECATED SOCIAL ROUTES (PROMPT 4) - Redirect    */}
          {/* ═══════════════════════════════════════════════════ */}
          <Route path="/feed" element={<Navigate to="/central" replace />} />
          <Route path="/home" element={<Navigate to="/central" replace />} />
          <Route path="/comunidades" element={<Navigate to="/" replace />} />
          <Route path="/futebol/comunidade" element={<Navigate to="/futebol" replace />} />
          <Route path="/natacao/comunidade" element={<Navigate to="/natacao" replace />} />
          <Route path="/perfil" element={<Navigate to="/configuracoes" replace />} />
          <Route path="/arena-profile" element={<Navigate to="/configuracoes" replace />} />
          <Route path="/chat" element={<ChatPage />} />

        {/* ═══════════════════════════════════════════════════ */}
        {/* CENTRAL HUB — Operational dashboard per role       */}
        {/* ═══════════════════════════════════════════════════ */}
        <Route path="/central" element={<CentralGate />} />

        {/* ═══════════════════════════════════════════════════ */}
        {/* VUKZ ASSIST — Central do Performance OS            */}
        {/* Substitui "Início" para os 4 perfis do lançamento   */}
        {/* ═══════════════════════════════════════════════════ */}
        <Route path="/assist" element={
          <RouteGuard rolesAllowed={[R.ATLETA, R.TREINADOR, R.PREPARADOR_FISICO, R.GESTOR, R.NUTRICIONISTA]}>
            <VukzAssistPage />
          </RouteGuard>
        } />

        {/* ═══════════════════════════════════════════════════ */}
        {/* HOME PAGES — Legacy (mantidos, redirigem → /assist) */}
        {/* ═══════════════════════════════════════════════════ */}
        <Route path="/athlete/home" element={<Navigate to="/athlete/today" replace />} />
        <Route path="/coach/home"   element={<Navigate to="/assist" replace />} />
        <Route path="/prep/home"    element={<Navigate to="/assist" replace />} />
        <Route path="/nutri/home"   element={
          <RouteGuard rolesAllowed={[R.NUTRICIONISTA]}><NutritionistHomePage /></RouteGuard>
        } />

        {/* NUTRICIONISTA — Workspace */}
        <Route path="/nutrition/dashboard" element={
          <RouteGuard rolesAllowed={[R.NUTRICIONISTA]}><NutritionDashboardPage /></RouteGuard>
        } />
        <Route path="/nutrition/athletes" element={
          <RouteGuard rolesAllowed={[R.NUTRICIONISTA]}><NutritionAthletesPage /></RouteGuard>
        } />
        <Route path="/nutrition/diets" element={
          <RouteGuard rolesAllowed={[R.NUTRICIONISTA]}><NutritionDietsPage /></RouteGuard>
        } />
        {/* N3: detalhe de leitura de UMA dieta (plan_id). Guard de acesso
            real é do backend (403 anti-enum); RouteGuard só filtra o perfil. */}
        <Route path="/nutrition/dieta/:planId" element={
          <RouteGuard rolesAllowed={[R.NUTRICIONISTA]}><NutritionPlanDetailPage /></RouteGuard>
        } />
        {/* N4: /nutrition/plans DESATIVADA — era a EntryPage (formulário de
            registro manual que NUNCA funcionou: enviava athlete_name texto
            livre sem athlete_id — backend exige athlete_id, todo submit era
            400; e os day_types dela ('training','recovery') violam o
            CK_nutri_day_type do banco). Zero dado gravado por ela. Se um dia
            existir registro-de-consumo-pelo-nutri, nasce tela nova com select
            de vínculo (getMyAthletes), não texto livre. */}
        <Route path="/nutrition/plans" element={<Navigate to="/nutrition/diets" replace />} />
        <Route path="/nutrition/alerts" element={
          <RouteGuard rolesAllowed={[R.NUTRICIONISTA]}><NutritionAlertsPage /></RouteGuard>
        } />
        {/* N4: /nutrition/notes era navegação fantasma (renderizava o
            Dashboard com outro label). Redirect até a tela de notas existir. */}
        <Route path="/nutrition/notes" element={<Navigate to="/nutrition/dashboard" replace />} />
        <Route path="/nutrition/novo" element={
          <RouteGuard rolesAllowed={[R.NUTRICIONISTA]}><NutritionPlanCreatePage /></RouteGuard>
        } />

        {/* BIOMECÂNICO — Workspace */}
        <Route path="/biomechanics/dashboard" element={
          <RouteGuard rolesAllowed={[R.BIOMECANICO]}><BiomechanicsDashboardPage /></RouteGuard>
        } />
        <Route path="/biomechanics/sessions" element={
          <RouteGuard rolesAllowed={[R.BIOMECANICO]}><BiomechanicsEntryPage /></RouteGuard>
        } />
        <Route path="/biomechanics/drills" element={
          <RouteGuard rolesAllowed={[R.BIOMECANICO]}><BiomechanicsDashboardPage /></RouteGuard>
        } />
        <Route path="/biomechanics/workspace" element={
          <RouteGuard rolesAllowed={[R.BIOMECANICO]}><BiomechanicsWorkspacePage /></RouteGuard>
        } />
        <Route path="/biomechanics/athletes" element={
          <RouteGuard rolesAllowed={[R.BIOMECANICO]}><BiomechanicsWorkspacePage /></RouteGuard>
        } />
        <Route path="/biomechanics/reports" element={
          <RouteGuard rolesAllowed={[R.BIOMECANICO]}><BiomechanicsDashboardPage /></RouteGuard>
        } />
        <Route path="/biomechanics/comparisons" element={
          <RouteGuard rolesAllowed={[R.BIOMECANICO]}><BiomechanicsDashboardPage /></RouteGuard>
        } />
        {/* Shared BIOMECANICO + TREINADOR performance view */}
        <Route path="/biomech/performance-dashboard" element={
          <RouteGuard rolesAllowed={[R.BIOMECANICO, R.TREINADOR]}><SwimBiomechDashboard /></RouteGuard>
        } />

        {/* ═══════════════════════════════════════════════════ */}
        {/* RBAC B2B — Áreas por Role                          */}
        {/* ═══════════════════════════════════════════════════ */}

        {/* ATLETA — Performance + Training routes */}
        <Route path="/my/performance" element={
          <RouteGuard rolesAllowed={[R.ATLETA, R.TREINADOR]}><AthletePerformancePage /></RouteGuard>
        } />
        {/* Athlete Shell v2 APOSENTADO (04/07/2026, pedido do user: "uma
            página só") — today/sessions/insights eram tabs órfãs de sidebar;
            tudo converge pra timeline unificada. Arquivos ficam no repo
            (remoção física em commit separado). */}
        <Route path="/my/training/:sportId/*" element={
          <RouteGuard rolesAllowed={[R.ATLETA]}><AthleteRedirect /></RouteGuard>
        } />
        <Route path="/my/wellness" element={
          <RouteGuard rolesAllowed={[R.ATLETA]}><WellnessPage /></RouteGuard>
        } />
        <Route path="/my/plano" element={
          <RouteGuard rolesAllowed={[R.ATLETA]}><AthleteIntegratedPlanPage /></RouteGuard>
        } />
        <Route path="/my/biomechanics" element={
          <RouteGuard rolesAllowed={[R.ATLETA]}><AthleteBiomechanicsPage /></RouteGuard>
        } />
        <Route path="/my/results" element={
          <RouteGuard rolesAllowed={[R.ATLETA]}><AthleteResultsPage /></RouteGuard>
        } />

        {/* ATLETA — Legacy redirects (retrocompatibilidade) */}
        <Route path="/athlete" element={
          <RouteGuard rolesAllowed={[R.ATLETA]}><AthleteRedirect /></RouteGuard>
        } />
        <Route path="/athlete/training/:planId" element={
          <RouteGuard rolesAllowed={[R.ATLETA]}><LegacyPlanRedirect /></RouteGuard>
        } />
        <Route path="/athlete/*" element={
          <RouteGuard rolesAllowed={[R.ATLETA]}><AthleteRedirect /></RouteGuard>
        } />

        {/* TREINADOR */}
        <Route path="/coach/performance" element={
          <RouteGuard rolesAllowed={[R.TREINADOR]}><CoachPerformancePage /></RouteGuard>
        } />
        <Route path="/coach/training/execute/:planId" element={
          <RouteGuard rolesAllowed={[R.TREINADOR, R.PREPARADOR_FISICO]}><TrainingExecutionPage mode="coach" /></RouteGuard>
        } />
        <Route path="/coach/training" element={
          <RouteGuard rolesAllowed={[R.TREINADOR, R.PREPARADOR_FISICO]}><CoachTrainingPage /></RouteGuard>
        } />
        <Route path="/coach/training/*" element={
          <RouteGuard rolesAllowed={[R.TREINADOR, R.PREPARADOR_FISICO]}><CoachTrainingPage /></RouteGuard>
        } />
        <Route path="/coach/groups" element={
          <RouteGuard rolesAllowed={[R.TREINADOR]}><CoachGroupsPage /></RouteGuard>
        } />
        <Route path="/coach/groups/:groupId" element={
          <RouteGuard rolesAllowed={[R.TREINADOR]}><CoachGroupDetailPage /></RouteGuard>
        } />
        <Route path="/coach" element={
          <RouteGuard rolesAllowed={[R.TREINADOR]}><CoachAreaPage /></RouteGuard>
        } />
        <Route path="/coach/athlete/:userId" element={
          <RouteGuard rolesAllowed={[R.TREINADOR, R.PREPARADOR_FISICO, R.GESTOR, R.BIOMECANICO, R.NUTRICIONISTA]}><CoachAthleteDetailPage /></RouteGuard>
        } />
        <Route path="/coach/*" element={
          <RouteGuard rolesAllowed={[R.TREINADOR]}><CoachAreaPage /></RouteGuard>
        } />

        {/* PREPARADOR_FISICO (P12 Extension) */}
        <Route path="/prep/training" element={
          <RouteGuard rolesAllowed={PREP_ROLES}><PrepTrainingPage /></RouteGuard>
        } />
        <Route path="/prep/builder" element={
          <RouteGuard rolesAllowed={PREP_ROLES}><PrepPhysicalBuilderPage /></RouteGuard>
        } />
        <Route path="/prep/groups" element={
          <RouteGuard rolesAllowed={PREP_ROLES}><PrepGroupsPage /></RouteGuard>
        } />
        <Route path="/prep/groups/:groupId" element={
          <RouteGuard rolesAllowed={PREP_ROLES}><PrepGroupDetailPage /></RouteGuard>
        } />
        {/* Monitoramento de Carga (07/08/2026): atrás de ENABLE_PREP_LOAD_UI
            (fail-closed). Tela misturava ACWR seedado do demo com painel
            computado vazio — some do nav E da rota até o ciclo execução→carga.
            Não é DEAD_CANDIDATE: PrepDashboardPage volta na reativação. */}
        <Route path="/prep/dashboard" element={
          FEATURES.ENABLE_PREP_LOAD_UI
            ? <RouteGuard rolesAllowed={PREP_ROLES}><PrepDashboardPage /></RouteGuard>
            : <Navigate to="/staff/today" replace />
        } />
        <Route path="/prep/load" element={
          FEATURES.ENABLE_PREP_LOAD_UI
            ? <RouteGuard rolesAllowed={PREP_ROLES}><PrepDashboardPage /></RouteGuard>
            : <Navigate to="/staff/today" replace />
        } />

        {/* STAFF — Today multi-papel (07/07/2026): roster-do-dia dos MEUS
            atletas. Escopo 100%server-side (vw_staff_accessible_athletes via
            JWT); nutri vê nutrição, demais veem treinos; gestor agrupado por
            categoria. HOME pós-login do staff. */}
        <Route path="/staff/today" element={
          <RouteGuard rolesAllowed={[R.TREINADOR, R.PREPARADOR_FISICO, R.GESTOR, R.NUTRICIONISTA]}><StaffTodayPage /></RouteGuard>
        } />

        {/* STAFF — Wellness Elenco (Coach + Prep) */}
        <Route path="/staff/wellness" element={
          <RouteGuard rolesAllowed={[R.TREINADOR, R.PREPARADOR_FISICO]}><SquadWellnessPage /></RouteGuard>
        } />

        {/* STAFF — Planejamento → redireciona para o Plano Estratégico unificado */}
        <Route path="/staff/planning" element={<Navigate to="/manager/plan" replace />} />

        {/* TREINOS — Lista, criação e detalhe de planos de treino */}
        <Route path="/treinos" element={
          <RouteGuard rolesAllowed={[R.GESTOR, R.TREINADOR, R.PREPARADOR_FISICO, R.ATLETA]}><TrainingPlanListPage /></RouteGuard>
        } />
        {/* /treinos/novo DEVE vir antes de /treinos/:id para não ser capturado como parâmetro */}
        <Route path="/treinos/novo" element={
          <RouteGuard rolesAllowed={[R.TREINADOR, R.PREPARADOR_FISICO]}><TrainingPlanCreatePage /></RouteGuard>
        } />
        {/* /treinos/modelos/:id idem — antes de /treinos/:id. Só staff que
            cria modelo edita modelo (ownership fino é do backend: 404). */}
        <Route path="/treinos/modelos/:id" element={
          <RouteGuard rolesAllowed={[R.TREINADOR, R.PREPARADOR_FISICO]}><TemplateDetailPage /></RouteGuard>
        } />
        <Route path="/treinos/:id" element={
          <RouteGuard rolesAllowed={[R.GESTOR, R.TREINADOR, R.PREPARADOR_FISICO, R.ATLETA]}><TrainingPlanDetailPage /></RouteGuard>
        } />

        {/* ATLETA — Página-do-dia (07/07/2026): HOME pós-login do atleta.
            Treino de hoje + dieta do dia (DayNutritionCard self/editable)
            + widgets. Timeline (/athlete/training) vira drill-down. */}
        <Route path="/athlete/today" element={
          <RouteGuard rolesAllowed={[R.ATLETA]}><TodayPage /></RouteGuard>
        } />

        {/* ATLETA — Minha Dieta (gate final demo jul/2026): detalhe completo
            do plano alimentar do atleta autenticado. Self-service: o backend
            resolve o atleta via g.user_id (day-plan/0?by=user). */}
        <Route path="/my/diet" element={
          <RouteGuard rolesAllowed={[R.ATLETA]}><MyDietPage /></RouteGuard>
        } />

        {/* STAFF — Dietas dos atletas (gate final demo jul/2026): consulta
            OPERACIONAL somente leitura. Mesmo componente nas 3 rotas; escopo
            é 100% server-side (/nutrition/my-athletes via SAA + day-plan
            revalida vínculo). Nutricionista NÃO usa esta página (tem as suas). */}
        <Route path="/coach/athlete-diets" element={
          <RouteGuard rolesAllowed={[R.TREINADOR]}><AthleteDietsPage /></RouteGuard>
        } />
        <Route path="/prep/athlete-diets" element={
          <RouteGuard rolesAllowed={[R.PREPARADOR_FISICO]}><AthleteDietsPage /></RouteGuard>
        } />
        <Route path="/manager/athlete-diets" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><AthleteDietsPage /></RouteGuard>
        } />

        {/* ATLETA - Training View */}
        {/* Camada 1 (04/07/2026): timeline unificada é A página de treino do
            atleta. execute/:planId era o canvas full-page — agora converge
            pra timeline (?plan= ancora no dia; estado temporal decide
            editável/readOnly). */}
        <Route path="/athlete/training" element={
          <RouteGuard rolesAllowed={[R.ATLETA]}><TrainingTimelinePage /></RouteGuard>
        } />
        <Route path="/athlete/training/execute/:planId" element={
          <RouteGuard rolesAllowed={[R.ATLETA]}><LegacyPlanRedirect /></RouteGuard>
        } />

        {/* STAFF — C3 (04/07/2026): a MESMA timeline em modo staff. Seletor
            de atletas (vw_staff_accessible_athletes via JWT), athlete_id
            revalidado por escopo no backend a cada chamada, viewer da
            execução decide edição (prep edita físico, coach edita técnico,
            cada um vê o do outro read-only). GESTOR (C3b, script 332):
            3ª branch da view dá escopo por CLUBE (role→todos os atletas);
            edição negada pelo viewer do servidor — gestor não está em
            nenhum conjunto de modalidade → tudo read-only por construção. */}
        <Route path="/staff/training" element={
          <RouteGuard rolesAllowed={[R.TREINADOR, R.PREPARADOR_FISICO, R.GESTOR, R.NUTRICIONISTA]}><TrainingTimelinePage staffMode /></RouteGuard>
        } />

        {/* GESTOR — C3c (05/07/2026): lentes agregadas de treino (por
            categoria / por grupo de staff), aderência REAL athlete-cêntrica,
            ACWR null-safe (physical_load_entries vazia no demo — acende na
            fase 'popular Acqua'). Drill-down → /staff/training?athlete= (a
            timeline C3a/C3b; viewer do servidor mantém gestor read-only). */}
        <Route path="/manager/training-overview" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><ManagerTrainingOverviewPage /></RouteGuard>
        } />

        {/* GESTOR — Performance Management */}
        <Route path="/manager" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><ManagerHomePage /></RouteGuard>
        } />
        <Route path="/manager/plan" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><GestorPlanPage /></RouteGuard>
        } />
        {/* G2 — Calendário Unificado (rota canônica). Gestão de temporadas/fases (G1) em /manage. */}
        <Route path="/manager/calendar" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><CalendarUnifiedPage /></RouteGuard>
        } />
        <Route path="/manager/calendar/manage" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><GestorCalendarPage /></RouteGuard>
        } />
        <Route path="/manager/strategic-plan" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><ManagerStrategicPlanPage /></RouteGuard>
        } />
        <Route path="/manager/categories" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><CategoriesPage /></RouteGuard>
        } />
        <Route path="/manager/categories/:categoryId" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><CategoryDetailPage /></RouteGuard>
        } />
        {/* Metodologia - FORA DO MVP (SEC-A lote desliga 11/07/2026). Backend atras de ENABLE_METHODOLOGY_API. */}
        <Route path="/manager/methodology" element={<Navigate to="/manager" replace />} />
        <Route path="/manager/interventions" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><InterventionsPage /></RouteGuard>
        } />
        <Route path="/manager/staff-scopes" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><GovernanceStaffScopesPage /></RouteGuard>
        } />
        <Route path="/manager/staff-permissions" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><StaffPermissionsPage /></RouteGuard>
        } />
        <Route path="/manager/roster" element={
          <RouteGuard rolesAllowed={[R.GESTOR, R.TREINADOR, R.PREPARADOR_FISICO]}><RosterPage /></RouteGuard>
        } />
        {/* S4: Equipe do gestor — listagem canonica (GET /gestor/staff, S3).
            Detalhe/editor de responsabilidades chega no S5.
            RosterPage preservada em /manager/roster (elenco + deep links). */}
        <Route path="/manager/staff" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><TeamPage /></RouteGuard>
        } />
        {/* S5: detalhe do profissional + editor de responsabilidades
            (GET /gestor/staff/{id}, preview obrigatório antes de salvar). */}
        <Route path="/manager/staff/:staffId" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><StaffDetailPage /></RouteGuard>
        } />
        <Route path="/manager/roster/athlete/:athleteId" element={
          <RouteGuard rolesAllowed={[R.GESTOR, R.TREINADOR, R.PREPARADOR_FISICO]}><AthleteProfilePage /></RouteGuard>
        } />
        <Route path="/atleta/perfil" element={
          <RouteGuard rolesAllowed={[R.ATLETA]}><AthleteProfilePage /></RouteGuard>
        } />
        <Route path="/manager/financial" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><FinancialPage /></RouteGuard>
        } />
        <Route path="/manager/support" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><SupportPage /></RouteGuard>
        } />
        {/* /manager/setup MOVIDO para o <Routes> externo (standalone, sem sidebar).
            Ver StandaloneAuthRoute + ADR-3 (auth-only, sem role GESTOR). */}
        <Route path="/manager/chat" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><ChatPage /></RouteGuard>
        } />
        <Route path="/manager/claim" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><ClaimOfficialDataPage /></RouteGuard>
        } />
        <Route path="/manager/*" element={
          <RouteGuard rolesAllowed={[R.GESTOR]}><ManagerHomePage /></RouteGuard>
        } />

        {/* RECRUITER */}
        <Route path="/recruiter" element={
          <RouteGuard rolesAllowed={[R.RECRUITER]} capability="arena.recruiter.read"><RecruiterAreaPage /></RouteGuard>
        } />
        <Route path="/recruiter/*" element={
          <RouteGuard rolesAllowed={[R.RECRUITER]} capability="arena.recruiter.read"><RecruiterAreaPage /></RouteGuard>
        } />

        {/* Biblioteca Digital (TREINADOR + GESTOR) */}
        <Route path="/library" element={
          <RouteGuard rolesAllowed={LIBRARY_ROLES}><LibraryHubPage /></RouteGuard>
        } />
        <Route path="/library/search" element={
          <RouteGuard rolesAllowed={LIBRARY_ROLES}><LibrarySearchPage /></RouteGuard>
        } />
        <Route path="/library/item/:itemId" element={
          <RouteGuard rolesAllowed={LIBRARY_ROLES}><LibraryItemDetailPage /></RouteGuard>
        } />
        <Route path="/library/create" element={
          <RouteGuard rolesAllowed={LIBRARY_ROLES}><LibraryCreateItemPage /></RouteGuard>
        } />
        <Route path="/library/protocols" element={
          <RouteGuard rolesAllowed={LIBRARY_ROLES}><TechnicalLibraryPage /></RouteGuard>
        } />
        {/* Metodologias - FORA DO MVP (SEC-A lote desliga 11/07/2026). Backend atras de ENABLE_METHODOLOGY_API. */}
        <Route path="/library/methodologies" element={<Navigate to="/library" replace />} />
        <Route path="/library/methodologies/:methodologyId" element={<Navigate to="/library" replace />} />

        {/* Research (consulta de dados auditada - requer capability) */}
        <Route path="/research" element={
          <RouteGuard capability="arena.researcher.read"><ResearchPage /></RouteGuard>
        } />
        
        {/* Diagnostic - Testa todos os endpoints */}
        <Route path="/diagnostic" element={<DiagnosticPage />} />
        
        {/* Directory Pages - Hidden from sidebar, accessible via URL */}
        <Route path="/jogadores" element={<PeopleDirectory />} />
        <Route path="/clubes" element={<OrganizationsPage />} />
        <Route path="/locais" element={<VenuesPage />} />
        <Route path="/esportes" element={<EsportesPage />} />
        
        {/* Criação de Instituições (admin-only, hidden from sidebar) */}
        <Route path="/institutions/create" element={
          <RouteGuard superAdminOnly><CreateInstitutionPage /></RouteGuard>
        } />
        
        {/* PRODUTOS - Creator legacy redirect to governance marketing hub */}
        <Route path="/creator" element={<Navigate to={OPS.marketingCreator} replace />} />
        <Route path="/creator/*" element={<Navigate to={OPS.marketingCreator} replace />} />
        {/* Analytics - User-facing performance analytics */}
        <Route path="/analytics" element={
          <RouteGuard rolesAllowed={ANALYTICS_ROLES}><AnalyticsHub /></RouteGuard>
        } />
        <Route path="/analytics/*" element={
          <RouteGuard rolesAllowed={ANALYTICS_ROLES}><AnalyticsHub /></RouteGuard>
        } />
        
        {/* PLAYBOOK SYSTEM - FORA DO MVP (SEC-A lote desliga 11/07/2026).
            Backend atras de ENABLE_PLAYBOOK_API fail-closed. URL fechada por
            redirect (regra tripla: menu + URL + backend). */}
        <Route path="/playbook/designer" element={<Navigate to="/central" replace />} />
        <Route path="/playbook/minhas-jogadas" element={<Navigate to="/central" replace />} />

        {/* TACTICS SYSTEM - Unified (Football + American Football) */}
        <Route path="/tactics/designer" element={
          <RouteGuard rolesAllowed={TACTICS_ROLES}><TacticsDesignerPage /></RouteGuard>
        } />
        <Route path="/tactics/plays/:id" element={
          <RouteGuard rolesAllowed={TACTICS_ROLES}><TacticsViewerPage /></RouteGuard>
        } />
        
        {/* Rota para hub de futebol */}
        <Route path="/futebol" element={<FootballHubPage />} />
        {/* Rota para Meus Campeonatos (contexto ativo) */}
        <Route path="/meus-campeonatos" element={<MyChampionshipsPage />} />
        {/* Rota unificada de competições */}
        <Route path="/competicoes" element={<SwimmingChampionshipsPage />} />
        {/* Rota SOCIAL para detalhes do campeonato de futebol */}
        <Route path="/futebol/campeonato/:leagueId/:season" element={<SocialChampionshipPage />} />
        {/* Rota para a página de detalhes da partida */}
        <Route path="/futebol/partida/:matchId" element={<MatchPage />} />
        {/* Rota para listagem de partidas de 2025 */}
        <Route path="/partidas-2025" element={<MatchesListPage />} />
        {/* Configurações de Usuário */}
        <Route path="/configuracoes" element={<UserProfilePage />} />
        {/* Meu Consumo de IA (C3 visão USER) */}
        <Route path="/meu-consumo" element={<MyUsagePage />} />
        {/* Consumo da Equipe (C3 visão TREINADOR) - 403 redirecionará via guard interno */}
        <Route path="/meu-consumo/equipe" element={<TeamUsagePage />} />
        {/* Consumo do Clube (C3 visão GESTOR ou TREINADOR-sem-gestor) */}
        <Route path="/meu-consumo/club" element={<ClubUsagePage />} />
        {/* Esportes */}
        <Route path="/natacao" element={<SwimmingPage />} />
        <Route path="/natacao/hub" element={<SwimmingHubPage />} />
        <Route path="/natacao/stats" element={<StatsPage />} />
        <Route path="/natacao/rankings" element={<SwimmingRankingsPage />} />
        <Route path="/natacao/resultados" element={<SwimmingCompetitionResultsPage />} />
        <Route path="/natacao/competicao/:id/:year" element={<SwimmingChampionshipDetailsPage />} />
        {/* Rota atualizada para perfil social do nadador */}
        <Route path="/natacao/nadador/:id" element={<PlayerDetailPage />} />
        <Route path="/nadador/:id" element={<PlayerDetailPage />} />
        {/* Locais - Nova nomenclatura social */}
        <Route path="/locais" element={<VenuesPage />} />
        <Route path="/locais/:venueId" element={<VenueDetailPage />} />
        {/* Polos Esportivos (legado - redirecionamento) */}
        <Route path="/polos-esportivos" element={<VenuesPage />} />
        <Route path="/polos-esportivos/:venueId" element={<VenueDetailPage />} />
        {/* Arenas (legado - redirecionamento) */}
        <Route path="/arenas" element={<VenuesPage />} />
        <Route path="/arenas/:venueId" element={<VenueDetailPage />} />
        {/* Venues (rota genérica - legado) */}
        <Route path="/venues" element={<VenuesPage />} />
        <Route path="/venues/:venueId" element={<VenueDetailPage />} />
        {/* Páginas Institucionais */}
        <Route path="/sobre" element={<AboutPage />} />
        <Route path="/privacidade" element={<PrivacyPolicyPage />} />
        <Route path="/termos" element={<TermsOfServicePage />} />
        <Route path="/sla" element={<SLAPage />} />
        <Route path="/contato" element={<ContactPage />} />
        {/* Configurações de Privacidade */}
        <Route path="/configuracoes/privacidade" element={<PrivacySettingsPage />} />
        {/* Swim Vision */}
        <Route path="/swim-vision/result/:sessionId" element={
          <RouteGuard rolesAllowed={VIDEO_ANALYSIS_ROLES}><RaceResultPage /></RouteGuard>
        } />
        <Route path="/swim-vision/race/:sessionId" element={
          <RouteGuard rolesAllowed={VIDEO_ANALYSIS_ROLES}><RaceDashboardPage /></RouteGuard>
        } />
        <Route path="/swim-vision/session/:sessionId/status" element={
          <RouteGuard rolesAllowed={VIDEO_ANALYSIS_ROLES}><SessionStatusPage /></RouteGuard>
        } />
        <Route path="/swim-vision/annotate/:sessionId" element={
          <RouteGuard rolesAllowed={VIDEO_ANALYSIS_ROLES}><SwimAnnotationsPage /></RouteGuard>
        } />
        <Route path="/swim-vision/sessions" element={
          <RouteGuard rolesAllowed={VIDEO_ANALYSIS_ROLES}><SwimVisionSessionsPage /></RouteGuard>
        } />
        <Route path="/swim-vision/dashboard" element={
          <RouteGuard rolesAllowed={VIDEO_ANALYSIS_ROLES}><SwimVisionDashboard /></RouteGuard>
        } />
        <Route path="/swim-vision/batch" element={
          <RouteGuard rolesAllowed={VIDEO_ANALYSIS_ROLES}><BatchAnalysisPage /></RouteGuard>
        } />
        <Route path="/swim-vision" element={
          <RouteGuard rolesAllowed={VIDEO_ANALYSIS_ROLES}><VideoAnalysisPage /></RouteGuard>
        } />
        {/* Legacy redirect */}
        <Route path="/analise-videos" element={
          <RouteGuard rolesAllowed={VIDEO_ANALYSIS_ROLES}><VideoAnalysisPage /></RouteGuard>
        } />
        {/* Agenda Unificada (VUKZ) — G2: para o GESTOR, Agenda deixou de existir
            como conceito de navegação; redireciona ao Calendário Unificado. */}
        <Route path="/agenda" element={<AgendaOrCalendarRedirect />} />
        {/* Cronograma redirecionado para Agenda (unificado) */}
        <Route path="/cronograma" element={<Navigate to="/agenda?tab=period" replace />} />
        {/* Treino Físico — Unified physical training (athlete + coach) */}
        <Route path="/treino-fisico" element={<TreinoFisicoPage />} />
        {/* Estatísticas (Insights) - Página antiga */}
        <Route path="/stats" element={<StatsPage />} />
        {/* Mind - AI Analytics */}
        <Route path="/mind" element={
          <RouteGuard rolesAllowed={ANALYTICS_ROLES}><Navigate to="/stats/mind" replace /></RouteGuard>
        } />
        <Route path="/stats/mind" element={
          <RouteGuard rolesAllowed={ANALYTICS_ROLES}><MindPage /></RouteGuard>
        } />
        <Route path="/stats/ranking/:eventId" element={<RankingDetailsPage />} />
        {/* Serviços */}
        <Route path="/servicos/editorial-engine" element={<EditorialEnginePage />} />
        {/* Track Vision — Athletics Biomechanics AI (VIDEO_ANALYSIS_ROLES guard, same as SwimVision) */}
        <Route path="/track-vision" element={
          <RouteGuard rolesAllowed={VIDEO_ANALYSIS_ROLES}><TrackVisionDashboard /></RouteGuard>
        } />
        <Route path="/track-vision/sessions" element={
          <RouteGuard rolesAllowed={VIDEO_ANALYSIS_ROLES}><TrackVisionSessionsPage /></RouteGuard>
        } />
        <Route path="/track-vision/sessions/:sessionId" element={
          <RouteGuard rolesAllowed={VIDEO_ANALYSIS_ROLES}><TrackVisionSessionPage /></RouteGuard>
        } />
        <Route path="/track-vision/sessions/:sessionId/annotate" element={
          <RouteGuard rolesAllowed={VIDEO_ANALYSIS_ROLES}><TrackVisionAnnotationPage /></RouteGuard>
        } />
        {/* Football Profile — dedicated player profile with stats */}
        <Route path="/futebol/jogador/:id" element={<FootballProfilePage />} />
        {/* Detail Pages (specific resource, keep) */}
        <Route path="/jogador/:id" element={<PlayerDetailPage />} />
        <Route path="/usuario/:userId" element={<CommunityUserDetailPage />} />
        <Route path="/clube/:id" element={<OrganizationDetailPage />} />
        <Route path="/clube/natacao/:id" element={<OrganizationDetailPage />} />
        <Route path="/clube/futebol/:id" element={<OrganizationDetailPage />} />
        <Route path="/organizacao/:id" element={<OrganizationDetailPage />} />
      </Routes>
      </React.Suspense>
      </Layout>
    </AccessGuard>
  );
}

// Redirect helper: /insights/:slug → /estudos/:slug (preserva slug)
function InsightSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/estudos/${slug}`} replace />;
}

function App() {
  console.log('[App.js] 🚀 App inicializada');
  console.log('[App.js] 📍 Rotas de autenticação configuradas: /login, /register');
  
  // LOG DE DIAGNÓSTICO (Prompt 3 - Anti-tela-branca)
  console.log('[App.js] Components status:', {
    ErrorBoundary: !!ErrorBoundary,
    AuthProvider: !!AuthProvider,
    Router: !!Router,
    Routes: !!Routes,
    Route: !!Route
  });
  
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <AnalyticsTracker />
          <GeoRedirect />
          <RBACProvider>
          <SportProvider>
          <ActiveContextProvider>
          <SportsLangProvider>
          <GovernanceLangProvider>
          <ProfileProvider>
          <Routes>
            {/* Página inicial — EN (default/Marketplace), PT (Brasil) */}
            <Route path="/" element={<VukzHomePage />} />
            <Route path="/pt" element={<PtVukzHomePage />} />
            <Route path="/en" element={<Navigate to="/" replace />} />

            {/* SHOWCASE — rotas de captura de marketing (dev-only via env) */}
            {SHOWCASE_MODE && (
              <>
                <Route path="/showcase/athlete/today" element={<AthleteTodayShowcase />} />
                <Route path="/showcase/coach/today" element={<CoachTodayShowcase />} />
                <Route path="/showcase/prep/today" element={<PrepTodayShowcase />} />
                <Route path="/showcase/nutritionist/today" element={<NutritionistTodayShowcase />} />
                <Route path="/showcase/manager/today" element={<ManagerTodayShowcase />} />
              </>
            )}

            {/* Legal pages EN */}
            <Route path="/en/privacy" element={<EnPrivacyPolicyPage />} />
            <Route path="/en/terms" element={<EnTermsOfServicePage />} />
            <Route path="/en/sla" element={<EnSLAPage />} />

            {/* Product landing pages EN */}
            <Route path="/en/sports-intelligence" element={<EnSportsLandingPage />} />
            <Route path="/en/governance-intelligence" element={<EnGovernanceLandingPage />} />
            
            {/* Stripe Billing — standalone (sem layout) */}
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            <Route path="/billing/cancel" element={<BillingCancelPage />} />

            {/* Landing Page alternativa (fundo azul, caso precise) */}
            <Route path="/landing" element={<LandingPage />} />
            
            {/* Rotas de autenticação (sem layout) */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPageV2 />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/invite-register" element={<InviteRegisterPage />} />

            {/* Club Setup Wizard — standalone (sem Layout/sidebar).
                Auth-only guard: o wizard CRIA o GESTOR no Step 1, então exigir
                role GESTOR aqui seria deadlock chicken-and-egg (ADR-3). */}
            <Route path="/manager/setup" element={
              <StandaloneAuthRoute><ClubSetupWizardPage /></StandaloneAuthRoute>
            } />
            <Route path="/pre-cadastro" element={<PreLaunchPage />} />
            <Route path="/solicitar-acesso" element={<RequestAccessPage lang="pt" />} />  {/* Captação comercial: cria APENAS lead, zero acesso */}
            <Route path="/request-access" element={<RequestAccessPage lang="en" />} />
            <Route path="/pending-approval" element={<PendingApprovalPage />} />  {/* NOVO: Tela "Em Breve" para usuários pendentes */}
            <Route path="/subscription-suspended" element={<SubscriptionSuspendedPage />} />  {/* Marketplace: assinatura suspensa */}
            <Route path="/marketplace/activate" element={<MarketplaceActivationPage />} />  {/* Azure Marketplace SaaS Landing Page */}
            
            {/* Estudos Públicos — Editorial Engine (sem auth) */}
            <Route path="/estudos" element={<PublicInsightsListPage />} />
            <Route path="/estudos/interativo/evolucao-tempos" element={<StudyAvgTimesEvolution />} />
            <Route path="/estudos/:slug" element={<PublicInsightArticlePage />} />
            {/* Redirects de compatibilidade (SEO + links antigos) */}
            <Route path="/insights" element={<Navigate to="/estudos" replace />} />
            <Route path="/insights/:slug" element={<InsightSlugRedirect />} />
            
            {/* GOVERNANÇA & FINOPS - Founder-only (Outlet pattern for RR v7) */}
            <Route path="/governance" element={<GovernanceLayoutRoute />}>
              <Route index element={<Navigate to={OPS.overview} replace />} />
              <Route path="overview" element={<GovernanceOverviewPage />} />
              <Route path="clientes" element={<GovernanceClientsPage />} />
              <Route path="clientes/:id" element={<GovernanceClientDetailPage />} />
              <Route path="exec/planner" element={<GovernancePlannerPage />} />
              {/* Gate P1: Builder desativado por padrão. No app cliente redireciona p/ Planner
                  (a página do Compete Minas é ops-only — guardrail testado na suíte de testes dela) */}
              <Route path="exec/builder" element={GOV_FEATURES.GOV_BUILDER_ENABLED ? <GovernanceBuilderPageV2 /> : <Navigate to="/governance/exec/planner" replace />} />
              <Route path="exec/brain" element={<BrainPage />} />
              <Route path="ops/agentes" element={<GovernanceAgentsPage />} />
              <Route path="ops/control-tower" element={<GovernanceControlTowerPage />} />
              <Route path="ops/observability" element={<GovernanceObservabilityPage />} />
              <Route path="ops/staff-scopes" element={<Navigate to="/manager/staff-scopes" replace />} />
              <Route path="fin" element={<GovernanceFinancePage />} />
              <Route path="fin/custos" element={<GovernanceFinancePage />} />
              <Route path="fin/receita" element={<GovernanceFinancePage />} />
              <Route path="fin/despesas" element={<GovernanceFinancePage />} />
              <Route path="fin/resultado" element={<GovernanceFinancePage />} />
              <Route path="fin/precificacao" element={<GovernancePricingPage />} />
              <Route path="billing" element={<BillingPortalPage />} />
              <Route path="pesquisa" element={<Navigate to={OPS.marketingHub} replace />} />
              <Route path="editorial-engine" element={<GovernanceEditorialEnginePage />} />
              <Route path="marketing" element={<Navigate to={OPS.marketingHub} replace />} />
              <Route path="marketing/overview" element={<Navigate to={OPS.marketingHub} replace />} />
              <Route path="marketing/hub" element={<GovernanceMarketingPage />} />
              <Route path="marketing/seo" element={<MarketingSeoDashboardPage />} />
              <Route path="marketing/creator" element={<CreatorHub />} />
              <Route path="marketing/analytics-tool" element={<AnalyticsHub />} />
              <Route path="marketing/editorial-engine" element={<GovernanceEditorialEnginePage />} />
              <Route path="marketing/pesquisa" element={<Navigate to={OPS.marketingHub} replace />} />
              <Route path="marketing/study-builder" element={<StudyBuilder />} />
              <Route path="marketing/content-studio" element={<GovernanceContentStudioPage />} />
              <Route path="content" element={<Navigate to={OPS.marketingHub} replace />} />
              <Route path="content/*" element={<Navigate to={OPS.marketingHub} replace />} />
              <Route path="investors" element={<Navigate to={OPS.investidores} replace />} />
              <Route path="investidores" element={<GovernanceInvestorsPage />} />
              <Route path="rh" element={<GovernanceRHPage />} />
              <Route path="rh/overview" element={<GovernanceRHPage />} />
              <Route path="rh/pessoas" element={<GovernanceRHPage />} />
              <Route path="rh/remuneracao" element={<GovernanceRHPage />} />
              <Route path="rh/planejamento" element={<GovernanceRHPage />} />
              <Route path="rh/permissoes" element={<GovernanceRHPage />} />
              <Route path="rh/financeiro" element={<GovernanceRHPage />} />
              <Route path="hr" element={<Navigate to={OPS.rh} replace />} />
              <Route path="comercial" element={<Navigate to={OPS.comercialPlanos} replace />} />
              <Route path="comercial/overview" element={<Navigate to={OPS.comercialPlanos} replace />} />
              <Route path="comercial/planos" element={<GovernanceComercialPage />} />
              <Route path="comercial/clientes" element={<GovernanceClientesPage />} />
              <Route path="comercial/leads" element={<SwimmingLeadsPage />} />
              <Route path="marketplace" element={<GovernanceMarketplacePage />} />
              <Route path="usage" element={<GovernanceUsageDashboardPage />} />
              <Route path="purview" element={<GovernancePurviewPage />} />
              <Route path="usuarios" element={<GovernanceUsersPage />} />
              <Route path="onboarding" element={<GovernanceOnboardingPage />} />
              <Route path="autorizacoes" element={<GovernanceAuthorizationsPage />} />
              {/* Gate P1: Builder legacy desativado por padrão — redireciona p/ Planner */}
              <Route path="builder-legacy" element={GOV_FEATURES.GOV_BUILDER_ENABLED ? <GovernanceBuilderPage /> : <Navigate to="/governance/exec/planner" replace />} />
              <Route path="ti/control-tower" element={<GovernanceControlTowerPage />} />
              <Route path="ti/observability" element={<GovernanceObservabilityPage />} />
              <Route path="ti/agentes" element={<GovernanceAgentsPage />} />
              <Route path="ti/monitoring" element={<GovernanceTIMonitoringPage />} />
              <Route path="planner" element={<Navigate to="/governance/exec/planner" replace />} />
              <Route path="builder" element={<Navigate to="/governance/exec/builder" replace />} />
              <Route path="agents" element={<Navigate to="/governance/ops/agentes" replace />} />
              <Route path="incidents" element={<Navigate to="/governance/ops/incidentes" replace />} />
              <Route path="control-tower" element={<Navigate to="/governance/ops/control-tower" replace />} />
              <Route path="costs" element={<Navigate to="/governance/fin/custos" replace />} />
              <Route path="research" element={<Navigate to={OPS.marketingHub} replace />} />
              <Route path="*" element={<div style={{padding:'2rem',color:'red',fontSize:'1.5rem'}}>⚠️ GOVERNANCE 404 — Path not matched: {window.location.pathname}</div>} />
            </Route>
            
            {/* Rotas principais (com layout e sidebar) */}
            <Route path="/*" element={<ProtectedAppRoutes />} />
          </Routes>
          </ProfileProvider>
          </GovernanceLangProvider>
          </SportsLangProvider>
          </ActiveContextProvider>
          </SportProvider>
          </RBACProvider>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;